'use client'

import { useCallback, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { apiClient } from '@/lib/api-client'
import { appendScopeFilterParams } from '@/lib/dashboard-scope-filters'
import { cn } from '@/lib/utils'

interface WeeklyKpi {
  week_number: number
  week_start?: string | null
  week_end?: string | null
  call_count: number
  average_score: number
  fatal_count: number
  fatal_percentage: number
}

interface Props {
  startDate: string
  endDate: string
  targetEmployeeId?: number
  selectedProduct?: string
  selectedSite?: string
}

function formatWeekRange(start?: string | null, end?: string | null) {
  if (!start || !end) return null
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
  return `${fmt(start)} – ${fmt(end)}`
}

function MiniStat({
  title,
  value,
  suffix = '',
  icon: Icon,
  colorClass,
  valueClassName,
}: {
  title: string
  value: number | string
  suffix?: string
  icon: React.ElementType
  colorClass: string
  valueClassName?: string
}) {
  return (
    <div className='group relative overflow-hidden rounded-xl border border-gray-100 bg-white p-4 shadow-sm'>
      <CardWatermark opacity={2} scale={0.8} />
      <div className='relative z-10 flex items-start justify-between gap-2'>
        <div className='min-w-0 space-y-1'>
          <p className='text-micro uppercase text-brand-muted'>{title}</p>
          <p className={cn('font-display text-2xl font-bold leading-none text-brand-navy', valueClassName)}>
            {value}{suffix}
          </p>
        </div>
        <div className={cn('shrink-0 rounded-lg p-2 text-white shadow-md', colorClass)}>
          <Icon className='h-4 w-4' strokeWidth={1.5} />
        </div>
      </div>
    </div>
  )
}

export function WeekWiseKpiCard({
  startDate,
  endDate,
  targetEmployeeId,
  selectedProduct,
  selectedSite,
}: Props) {
  const [weeks, setWeeks] = useState<WeeklyKpi[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    if (!startDate || !endDate) return
    setLoading(true)
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
      if (targetEmployeeId) params.set('target_employee_id', String(targetEmployeeId))
      appendScopeFilterParams(params, selectedProduct, selectedSite)
      const data = await apiClient.get<WeeklyKpi[]>(`/api/analysis/stats/weekly?${params}`)
      setWeeks(data)
    } catch {
      setWeeks([])
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, targetEmployeeId, selectedProduct, selectedSite])

  useEffect(() => {
    load()
  }, [load])

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={2} />
        <CardHeader className='relative z-10 pb-2'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.calendar className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            Week-wise Overview
          </CardTitle>
          <p className='mt-1 text-xs text-muted-foreground'>
            Audit count, score, fatals and fatal % per week
          </p>
        </CardHeader>
        <CardContent className='relative z-10 space-y-6 pt-2'>
          {loading ? (
            <div className='space-y-4'>
              {[1, 2].map((i) => (
                <Skeleton key={i} className='h-32 w-full rounded-xl' />
              ))}
            </div>
          ) : weeks.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-10 text-center'>
              <Icons.inbox className='mb-3 h-10 w-10 text-gray-200' />
              <p className='text-sm text-muted-foreground'>No weekly data for selected range</p>
            </div>
          ) : (
            weeks.map((week) => {
              const range = formatWeekRange(week.week_start, week.week_end)
              return (
                <div key={week.week_number} className='space-y-3'>
                  <div className='flex items-baseline gap-2'>
                    <h3 className='text-sm font-semibold text-brand-navy'>
                      Week {week.week_number}
                    </h3>
                    {range && (
                      <span className='text-xs text-muted-foreground'>{range}</span>
                    )}
                  </div>
                  <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
                    <MiniStat
                      title='Audit Count'
                      value={week.call_count}
                      icon={Icons.messageSquare}
                      colorClass='bg-brand-navy'
                    />
                    <MiniStat
                      title='Avg Score'
                      value={Math.round(week.average_score)}
                      suffix='%'
                      icon={Icons.activity}
                      colorClass='bg-brand-cornflower'
                    />
                    <MiniStat
                      title='Fatal Count'
                      value={week.fatal_count}
                      icon={Icons.alertCircle}
                      colorClass='bg-red-500'
                      valueClassName='text-red-600'
                    />
                    <MiniStat
                      title='Fatal %'
                      value={week.fatal_percentage}
                      suffix='%'
                      icon={Icons.alertTriangle}
                      colorClass='bg-amber-500'
                      valueClassName={week.fatal_percentage >= 50 ? 'text-red-600' : 'text-brand-navy'}
                    />
                  </div>
                </div>
              )
            })
          )}
        </CardContent>
      </Card>
    </motion.div>
  )
}
